import { EvseStatus, SiteType, VehicleOperationalStatus } from '@core/api/single-site/single-site.enums';
import { ChargerOperationalStatus } from '@core/api/sites/sites.enums';
import { SiteStats } from '@core/api/sites/sites.models';

import {
  mapConnectorTypeApiModelEnumToStoreModelEnum,
  mapEvseStatusApiModelEnumToStoreModelEnum,
  mapHealthGradeApiModelEnumToStoreModelEnum,
  mapSiteTypeApiModelEnumToStoreModelEnum,
  mapVehicleOperationalStatusApiModelEnumToStoreModelEnum,
} from '@domain/single-site/single-site-enums.mappings';
import { mapGeoPositionApiModelToStoreModel } from '@domain/single-site/single-site-models.mappings';
import { EvseStatusStoreModelEnum, VehicleOperationalStatusStoreModelEnum } from '@domain/single-site/single-site.enums';

import { wToKw } from '@shared/utils/w-to-kw';
import { whToKwh } from '@shared/utils/wh-to-kwh';

import {
  mapBessStatusApiModelEnumToStoreModelEnum,
  mapChargerOperationalStatusApiModelEnumToStoreModelEnum,
  mapSiteAlertStateApiModelEnumToStoreModelEnum,
  mapSiteReadinessStateApiModelEnumToStoreModelEnum,
} from './sites-enums.mappings';
import { ChargerOperationalStatusStoreModelEnum } from './sites.enums';
import { SiteStatsStoreModel } from './sites.models';

export const mapSiteStatsApiModelToStoreModel = (siteStats: SiteStats): SiteStatsStoreModel => {
  return {
    id: siteStats.id,
    name: siteStats.name,
    siteType: mapSiteTypeApiModelEnumToStoreModelEnum(siteStats.siteType),
    timeZone: siteStats.timeZone,
    region: siteStats.region,
    tenant: siteStats.tenant,
    alertState: mapSiteAlertStateApiModelEnumToStoreModelEnum(siteStats.alertState),
    vehiclesPerState:
      siteStats.siteType === SiteType.Fleet
        ? siteStats.vehiclesPerState &&
          Object.entries(siteStats.vehiclesPerState).reduce(
            (acc, [key, value]) => {
              const typedKey = key as VehicleOperationalStatus;
              const mappedKey = mapVehicleOperationalStatusApiModelEnumToStoreModelEnum(typedKey);
              return { ...acc, [mappedKey]: value };
            },
            {} as Record<VehicleOperationalStatusStoreModelEnum, number>
          )
        : undefined,
    evsesPerState:
      siteStats.siteType === SiteType.Public
        ? siteStats.evsesPerState &&
          Object.entries(siteStats.evsesPerState).reduce(
            (acc, [key, value]) => {
              const typedKey = key as EvseStatus;
              const mappedKey = mapEvseStatusApiModelEnumToStoreModelEnum(typedKey);
              return { ...acc, [mappedKey]: value };
            },
            {} as Record<EvseStatusStoreModelEnum, number>
          )
        : undefined,
    centerCoordinates: mapGeoPositionApiModelToStoreModel(siteStats.centerCoordinates),
    pocPowerKw: wToKw(siteStats.pocPower),
    pocLimitKw: wToKw(siteStats.pocLimit),
    pocHealthGrade: mapHealthGradeApiModelEnumToStoreModelEnum(siteStats.pocHealthGrade),
    bessPowerKw: siteStats.bessPower && wToKw(siteStats.bessPower),
    bessSocPercentage: siteStats.bessSocPercentage,
    bessSocEnergyKwh: siteStats.bessSocEnergy && whToKwh(siteStats.bessSocEnergy),
    bessSocHealthGrade: siteStats.bessSocHealthGrade && mapHealthGradeApiModelEnumToStoreModelEnum(siteStats.bessSocHealthGrade),
    bessStatus: siteStats.bessStatus && mapBessStatusApiModelEnumToStoreModelEnum(siteStats.bessStatus),
    evLoadKw: wToKw(siteStats.evLoad),
    readinessState: mapSiteReadinessStateApiModelEnumToStoreModelEnum(siteStats.readinessState),
    predictedDeparture: siteStats.predictedDeparture,
    predictedDepartureHealthGrade:
      siteStats.predictedDepartureHealthGrade && mapHealthGradeApiModelEnumToStoreModelEnum(siteStats.predictedDepartureHealthGrade),
    activeEvses: siteStats.activeEvses,
    totalEvses: siteStats.totalEvses,
    evsesHealthGrade: mapHealthGradeApiModelEnumToStoreModelEnum(siteStats.evsesHealthGrade),
    chargersPerState: Object.entries(siteStats.chargersPerState).reduce(
      (acc, [key, value]) => {
        const typedKey = key as ChargerOperationalStatus;
        const mappedKey = mapChargerOperationalStatusApiModelEnumToStoreModelEnum(typedKey);
        return { ...acc, [mappedKey]: value };
      },
      {} as Record<ChargerOperationalStatusStoreModelEnum, number>
    ),
    performancePercentage: siteStats.performancePercentage,
    criticalErrorsCount: siteStats.criticalErrorsCount,
    majorErrorsCount: siteStats.majorErrorsCount,
    dominantConnectorType: siteStats.dominantConnectorType && mapConnectorTypeApiModelEnumToStoreModelEnum(siteStats.dominantConnectorType),
    features: siteStats.features,
  };
};
